import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  ClipboardList, CheckCircle, XCircle, Clock, Eye, MapPin,
  Loader2, Mail, PlusCircle, ChevronRight, Calendar, Package,
  DollarSign, CreditCard, ExternalLink, ShieldOff, Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusStyles = {
  pending: 'bg-amber-100 text-amber-700',
  reviewed: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  awaiting_payment: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  // Legacy alias — older rows in the DB may still carry this status.
  approval_pending_payment: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  declined: 'bg-red-100 text-red-700',
  fulfilled: 'bg-purple-100 text-purple-700',
  completed: 'bg-purple-100 text-purple-700',
};

const AdminJobRequests = () => {
  const { getAuthHeader, adminRole } = useAuth();
  const navigate = useNavigate();
  const isFinanceAdmin = adminRole === 'admin';
  const canApprove = adminRole === 'admin' || adminRole === 'manager';

  // Auditors are READ-ONLY everywhere and per user spec are not even
  // allowed on this page — bounce them back to the dashboard.
  useEffect(() => {
    if (adminRole === 'auditor') {
      toast.error('Auditors do not have access to Job Requests.');
      navigate('/admin/dashboard', { replace: true });
    }
  }, [adminRole, navigate]);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);
  // Send-Invoice modal state — opened from the admin-only
  // "Send Invoice" button. Submitting creates the Stripe payment
  // link and emails the customer.
  const [invoiceModal, setInvoiceModal] = useState(null); // { request, amount, submitting, lastResult }

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/job-requests`, getAuthHeader());
      setRequests(res.data);
    } catch { toast.error('Failed to load job requests'); }
    finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleStatusUpdate = async (e, requestId, newStatus) => {
    e.stopPropagation();
    setUpdatingId(requestId);
    try {
      await axios.put(`${API}/api/admin/job-requests/${requestId}/status?status=${newStatus}`, {}, getAuthHeader());
      toast.success(`Request ${newStatus}. Client notified.`);
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally { setUpdatingId(null); }
  };

  const openApproveModal = (e, req) => {
    e.stopPropagation();
    // No modal — approve is one-click for managers + admins. The
    // dedicated Send Invoice button below opens the financial modal
    // (admin only).
    handleApprove(req);
  };

  const handleApprove = async (req) => {
    if (!canApprove) return;
    setUpdatingId(req.id);
    try {
      const res = await axios.post(
        `${API}/api/admin/job-requests/${req.id}/approve`, {},
        getAuthHeader(),
      );
      if (res.data?.already_approved) {
        toast.info('Already approved.');
      } else {
        toast.success('Request approved. Customer notified.');
      }
      fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally { setUpdatingId(null); }
  };

  const openInvoiceModal = (e, req) => {
    e.stopPropagation();
    if (!isFinanceAdmin) return;
    setInvoiceModal({ request: req, amount: '', description: '', lineItems: [], submitting: false, lastResult: null });
  };

  const updateLineItem = (idx, patch) => {
    setInvoiceModal((m) => ({
      ...m,
      lineItems: m.lineItems.map((li, i) => (i === idx ? { ...li, ...patch } : li)),
    }));
  };

  const submitSendInvoice = async () => {
    if (!invoiceModal) return;
    const amt = parseFloat(invoiceModal.amount);
    if (!amt || amt <= 0) {
      toast.error('Enter an invoice amount greater than 0.');
      return;
    }
    const items = [];
    for (const li of invoiceModal.lineItems || []) {
      const label = (li.label || '').trim();
      const liAmt = parseFloat(li.amount);
      if (!label && !li.amount) continue; // fully empty row — skip silently
      if (!label) { toast.error('Every line item needs a description.'); return; }
      if (!liAmt || liAmt <= 0) { toast.error(`Line item "${label}" needs an amount greater than 0.`); return; }
      items.push({ label, amount: liAmt });
    }
    setInvoiceModal({ ...invoiceModal, submitting: true });
    try {
      const res = await axios.post(
        `${API}/api/admin/job-requests/${invoiceModal.request.id}/send-invoice`,
        {
          invoice_amount: amt,
          origin_url: window.location.origin,
          description: (invoiceModal.description || '').trim() || null,
          line_items: items.length ? items : null,
        },
        getAuthHeader(),
      );
      toast.success('Invoice sent. Stripe payment link emailed to customer.');
      setInvoiceModal({
        ...invoiceModal,
        submitting: false,
        lastResult: res.data,
      });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice');
      setInvoiceModal({ ...invoiceModal, submitting: false });
    }
  };

  const handleCreateJobFromRequest = (req) => {
    // Store request data in sessionStorage and navigate to create job page
    const prefillData = {
      request_id: req.id,
      title: req.title || '',
      client: req.client_company || req.client_name || '',
      client_contact_name: req.client_name || '',
      client_email: req.client_email || '',
      location: req.location || '',
      description: req.description || '',
      deliveries: req.estimated_deliveries || 0,
      delivery_boundary: req.delivery_boundary || [],
      notes: req.notes || '',
      preferred_date: req.preferred_date || '',
    };
    sessionStorage.setItem('clientRequestData', JSON.stringify(prefillData));
    navigate('/admin/create-job?from_request=true');
  };

  const filtered = filter === 'all'
    ? requests
    : requests.filter(r =>
        r.status === filter
        // Treat legacy `approval_pending_payment` rows as `awaiting_payment` for the filter chip.
        || (filter === 'awaiting_payment' && r.status === 'approval_pending_payment'),
      );
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const reviewedCount = requests.filter(r => r.status === 'reviewed').length;

  // Hide the Awaiting-Payment / Approved (Paid) filter chips for
  // managers + auditors so they never even see the financial axis.
  const filters = [
    { key: 'all', label: 'All', count: requests.length },
    { key: 'pending', label: 'Pending', count: pendingCount },
    { key: 'reviewed', label: 'Reviewed', count: reviewedCount },
    ...(isFinanceAdmin ? [
      { key: 'awaiting_payment', label: 'Awaiting Payment',
        count: requests.filter(r => r.status === 'awaiting_payment' || r.status === 'approval_pending_payment').length },
    ] : []),
    { key: 'approved', label: isFinanceAdmin ? 'Approved (Paid)' : 'Approved',
      count: requests.filter(r => r.status === 'approved').length },
    { key: 'fulfilled', label: 'Fulfilled', count: requests.filter(r => r.status === 'fulfilled').length },
    { key: 'declined', label: 'Declined', count: requests.filter(r => r.status === 'declined').length },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job Requests</h1>
          <p className="text-sm text-gray-500 mt-1">Client-submitted delivery job requests</p>
        </div>
        {pendingCount > 0 && (
          <Badge className="bg-amber-100 text-amber-700 text-sm px-3 py-1">
            {pendingCount} pending
          </Badge>
        )}
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2" data-testid="request-filters">
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              filter === f.key
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            data-testid={`filter-${f.key}`}
          >
            {f.label} {f.count > 0 && `(${f.count})`}
          </button>
        ))}
      </div>

      {/* Request List */}
      {loading ? (
        <div className="flex justify-center p-16"><Loader2 size={28} className="animate-spin text-purple-500" /></div>
      ) : filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map(req => {
            const isPending = req.status === 'pending';
            const isReviewed = req.status === 'reviewed';
            const isApproved = req.status === 'approved';
            const isAwaitingPayment = req.status === 'awaiting_payment' || req.status === 'approval_pending_payment';
            const isFulfilled = req.status === 'fulfilled';
            const isDeclined = req.status === 'declined';
            const canApproveThis = (isPending || isReviewed) && canApprove;
            // Generate Invoice is available on every status except
            // awaiting_payment (that row already has a live checkout link).
            const canSendInvoiceForThis = !isAwaitingPayment && isFinanceAdmin;
            const canCreateJob = isPending || isReviewed;
            return (
              <Card
                key={req.id}
                className={`p-5 border-0 shadow-sm hover:shadow-md transition-all cursor-pointer ${isPending ? 'ring-1 ring-amber-200' : ''}`}
                onClick={() => handleCreateJobFromRequest(req)}
                data-testid={`request-card-${req.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <p className="font-semibold text-gray-900 text-base">{req.title}</p>
                      <Badge className={`${statusStyles[req.status] || 'bg-gray-100 text-gray-600'} text-xs capitalize`}>
                        {(req.status || '').replace(/_/g, ' ')}
                      </Badge>
                      {req.delivery_boundary && req.delivery_boundary.length >= 3 && (
                        <Badge className="bg-purple-100 text-purple-700 text-xs"><MapPin size={10} className="mr-0.5" /> Zone</Badge>
                      )}
                      {/* Financial badges are admin-only — never shown to managers/auditors. */}
                      {isFinanceAdmin && req.invoice_amount && (
                        <Badge
                          className="bg-emerald-50 text-emerald-700 text-xs border border-emerald-200"
                          data-testid={`invoice-badge-${req.id}`}
                        >
                          <DollarSign size={10} className="mr-0.5" />
                          ${Number(req.invoice_amount).toFixed(2)} invoice
                          {req.invoice_paid ? ' • Paid' : ' • Awaiting payment'}
                        </Badge>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mb-2">
                      <span className="flex items-center gap-1"><Mail size={12} /> {req.client_name} {req.client_company ? `(${req.client_company})` : ''}</span>
                      {req.location && <span className="flex items-center gap-1"><MapPin size={12} /> {req.location}</span>}
                      {req.estimated_deliveries && <span className="flex items-center gap-1"><Package size={12} /> {req.estimated_deliveries} deliveries</span>}
                      {req.preferred_date && <span className="flex items-center gap-1"><Calendar size={12} /> {req.preferred_date}</span>}
                      <span className="flex items-center gap-1"><Clock size={12} /> {new Date(req.created_at).toLocaleDateString()}</span>
                    </div>

                    {req.description && <p className="text-sm text-gray-600 line-clamp-2">{req.description}</p>}
                    {req.notes && <p className="text-xs text-gray-400 mt-1 italic">Note: {req.notes}</p>}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {canCreateJob && (
                      <Button
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-500 text-white"
                        onClick={(e) => { e.stopPropagation(); handleCreateJobFromRequest(req); }}
                        data-testid={`create-from-request-${req.id}`}
                      >
                        <PlusCircle size={14} className="mr-1" /> Create Job
                      </Button>
                    )}
                    {/* Step 1: Approve — manager OR admin */}
                    {canApproveThis && (
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-500 text-white"
                        onClick={(e) => openApproveModal(e, req)}
                        disabled={updatingId === req.id}
                        data-testid={`approve-btn-${req.id}`}
                      >
                        {updatingId === req.id
                          ? <Loader2 size={14} className="animate-spin mr-1" />
                          : <CheckCircle size={14} className="mr-1" />}
                        Approve
                      </Button>
                    )}
                    {/* Step 2: Send Invoice — ADMIN ONLY (managers/auditors cannot see this button) */}
                    {canSendInvoiceForThis && (
                      <Button
                        size="sm"
                        className="bg-violet-600 hover:bg-violet-500 text-white"
                        onClick={(e) => openInvoiceModal(e, req)}
                        disabled={updatingId === req.id}
                        data-testid={`send-invoice-btn-${req.id}`}
                      >
                        <CreditCard size={14} className="mr-1" /> Generate Invoice
                      </Button>
                    )}
                    {/* Manager view: subtle indicator that financial actions exist but are not theirs to take. */}
                    {!isAwaitingPayment && !isFinanceAdmin && canApprove && (
                      <span
                        className="text-[11px] text-gray-400 italic"
                        title="Only the admin role can send invoices and see financial data."
                        data-testid={`finance-locked-${req.id}`}
                      >
                        <ShieldOff size={12} className="inline mr-0.5" /> Invoice: admin only
                      </span>
                    )}
                    {isAwaitingPayment && isFinanceAdmin && req.stripe_checkout_url && (
                      <a
                        href={req.stripe_checkout_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-900 border border-emerald-200 rounded-md px-2 py-1"
                        data-testid={`copy-checkout-${req.id}`}
                      >
                        <ExternalLink size={12} /> Checkout link
                      </a>
                    )}
                    {isPending && (
                      <Button
                        size="sm" variant="outline"
                        onClick={(e) => handleStatusUpdate(e, req.id, 'reviewed')}
                        disabled={updatingId === req.id}
                        className="text-blue-600 border-blue-200 hover:bg-blue-50"
                        data-testid={`review-btn-${req.id}`}
                      >
                        {updatingId === req.id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} className="mr-1" />}
                        Review
                      </Button>
                    )}
                    {(isPending || isReviewed) && (
                      <Button
                        size="sm" variant="outline"
                        onClick={(e) => handleStatusUpdate(e, req.id, 'declined')}
                        disabled={updatingId === req.id}
                        className="text-red-500 border-red-200 hover:bg-red-50"
                        data-testid={`decline-btn-${req.id}`}
                      >
                        <XCircle size={14} />
                      </Button>
                    )}
                    <ChevronRight size={18} className="text-gray-300" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="p-16 border-0 shadow-sm text-center">
          <ClipboardList size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg font-medium">No job requests</p>
          <p className="text-sm text-gray-400 mt-1">Client-submitted requests will appear here</p>
        </Card>
      )}

      {/* ========== Send Invoice Modal (admin only) ========== */}
      <Dialog
        open={!!invoiceModal}
        onOpenChange={(open) => !open && setInvoiceModal(null)}
      >
        <DialogContent data-testid="send-invoice-modal">
          <DialogHeader>
            <DialogTitle>Generate Invoice</DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Generates a Stripe payment link, emails the customer, and adds
              the invoice to the Invoices section.
              {invoiceModal?.request?.status === 'fulfilled' && (
                <span className="block mt-1 text-purple-700 font-medium" data-testid="invoice-fulfilled-note">
                  This request is marked Fulfilled — generating an invoice won't change its status.
                </span>
              )}
              {invoiceModal?.request?.applied_coupon_code && (
                <span className="block mt-1 text-emerald-700 font-medium">
                  This customer redeemed coupon{' '}
                  <span className="font-mono">{invoiceModal.request.applied_coupon_code}</span>{' '}
                  — $100 off will be auto-deducted from the amount you enter.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {invoiceModal && !invoiceModal.lastResult && (
            <div className="space-y-4 mt-2">
              <div className="bg-gray-50 rounded-md px-3 py-2 text-sm">
                <p className="text-gray-700"><strong>Request:</strong> {invoiceModal.request.title}</p>
                <p className="text-gray-700"><strong>Customer:</strong> {invoiceModal.request.client_name} &lt;{invoiceModal.request.client_email}&gt;</p>
                {invoiceModal.request.estimated_deliveries && (
                  <p className="text-gray-500 text-xs mt-1">
                    Estimated deliveries: {invoiceModal.request.estimated_deliveries.toLocaleString()}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Invoice Amount (USD)
                </label>
                <div className="relative">
                  <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    value={invoiceModal.amount}
                    onChange={(e) => setInvoiceModal({ ...invoiceModal, amount: e.target.value })}
                    placeholder="e.g. 1900.00"
                    className="pl-9"
                    data-testid="invoice-amount-input"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  At $0.39/door, {invoiceModal.request.estimated_deliveries
                    ? `${invoiceModal.request.estimated_deliveries.toLocaleString()} doors = $${(invoiceModal.request.estimated_deliveries * 0.39).toFixed(2)}`
                    : 'enter the agreed quote'}.
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-700">
                    Additional Line Items <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setInvoiceModal({ ...invoiceModal, lineItems: [...invoiceModal.lineItems, { label: '', amount: '' }] })}
                    className="text-xs font-medium text-purple-700 hover:text-purple-900 inline-flex items-center gap-1"
                    data-testid="invoice-add-line-item-btn"
                  >
                    <PlusCircle size={13} /> Add line item
                  </button>
                </div>
                {invoiceModal.lineItems.length > 0 && (
                  <div className="space-y-2">
                    {invoiceModal.lineItems.map((li, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`invoice-line-item-row-${idx}`}>
                        <Input
                          value={li.label}
                          onChange={(e) => updateLineItem(idx, { label: e.target.value })}
                          placeholder="e.g. Rush fee, Design, Print"
                          maxLength={120}
                          className="flex-1"
                          data-testid={`invoice-line-item-label-${idx}`}
                        />
                        <div className="relative w-32">
                          <DollarSign size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                          <Input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={li.amount}
                            onChange={(e) => updateLineItem(idx, { amount: e.target.value })}
                            placeholder="0.00"
                            className="pl-7"
                            data-testid={`invoice-line-item-amount-${idx}`}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => setInvoiceModal({ ...invoiceModal, lineItems: invoiceModal.lineItems.filter((_, i) => i !== idx) })}
                          className="text-red-400 hover:text-red-600 p-1"
                          data-testid={`invoice-line-item-remove-${idx}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {(() => {
                  const base = parseFloat(invoiceModal.amount) || 0;
                  const itemsSum = invoiceModal.lineItems.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
                  const total = base + itemsSum;
                  if (!itemsSum) return null;
                  return (
                    <p className="text-xs text-gray-600 mt-2 text-right" data-testid="invoice-total-preview">
                      Invoice total: <span className="font-bold text-gray-900">${total.toFixed(2)}</span>
                      {invoiceModal.request.applied_coupon_code && <span className="text-emerald-700"> (before $100 coupon)</span>}
                    </p>
                  );
                })()}
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Description <span className="text-gray-400 font-normal">(optional — shown on the invoice and in the customer email)</span>
                </label>
                <textarea
                  value={invoiceModal.description}
                  onChange={(e) => setInvoiceModal({ ...invoiceModal, description: e.target.value })}
                  placeholder="e.g. 5,000 door hangers — Rowlett TX, includes design + print"
                  maxLength={300}
                  rows={2}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="invoice-description-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setInvoiceModal(null)}
                  data-testid="invoice-modal-cancel"
                >
                  Cancel
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-500 text-white"
                  onClick={submitSendInvoice}
                  disabled={invoiceModal.submitting}
                  data-testid="invoice-modal-submit"
                >
                  {invoiceModal.submitting
                    ? (<><Loader2 size={14} className="animate-spin mr-1" /> Creating link…</>)
                    : (<><CreditCard size={14} className="mr-1" /> Generate Invoice</>)}
                </Button>
              </div>
            </div>
          )}
          {invoiceModal && invoiceModal.lastResult && (
            <div className="space-y-3 mt-2" data-testid="invoice-modal-success">
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm">
                <p className="text-emerald-800 font-semibold flex items-center gap-1.5">
                  <CheckCircle size={16} /> Invoice sent
                </p>
                <p className="text-emerald-700 text-xs mt-1">
                  Customer was emailed a secure Stripe payment link for ${invoiceModal.lastResult.invoice_amount.toFixed(2)}.
                  {invoiceModal.lastResult.invoice_discount > 0 && (
                    <> ($100 coupon auto-deducted from ${invoiceModal.lastResult.invoice_subtotal.toFixed(2)} subtotal.)</>
                  )}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">Stripe Checkout link (for reference):</p>
                <a
                  href={invoiceModal.lastResult.checkout_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-purple-700 hover:text-purple-900 break-all"
                  data-testid="invoice-modal-checkout-link"
                >
                  <ExternalLink size={12} /> {invoiceModal.lastResult.checkout_url}
                </a>
              </div>
              <div className="flex justify-end pt-2">
                <Button onClick={() => setInvoiceModal(null)} data-testid="invoice-modal-close">
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminJobRequests;
